import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { styles } from "@insite/content-library/Widgets/ContactUs/AvailableTopics";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    title = "title",
    heading = "heading",
    buttonLabel = "buttonLabel",
    buttonVariant = "variant",
    buttonLink = "buttonLink",
    disableButton = "disableButton",
    openNewTab = "openNewTab",
}

interface Props extends WidgetProps {
    fields: {
        [fields.background]: "image";
        [fields.image]: string;
        [fields.disableButton]: boolean;
        [fields.title]: string;
        [fields.heading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.buttonLink]: LinkFieldValue;
        [fields.openNewTab]: boolean;
    };
}

export interface ImageHoverBoxStyles {
    wrapperStyles?: GridContainerProps;
    titleGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    linkGridItem?: GridItemProps;
    imageGridItem?: GridItemProps;
    titleStyles?: TypographyProps;
    headingStyles?: InjectableCss;
    cickableWrapper?: InjectableCss;
    styleButton?: InjectableCss;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    categoryImage?: LazyImageProps;
}

export const ImageHoverBoxStyles: ImageHoverBoxStyles = {
    wrapperStyles: {
        css: css`
            padding: 20px;
            height: 100%;
            border: 1px solid ${getColor("common.border")};
            border-radius: 10px;
        `,
    },
    cickableWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 1rem 1rem 0px;
            min-height: 76px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding: 0px 1rem;
        `,
    },
    headingStyles: {
        css: css`
            color: ${getColor("primary")};
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            height: 72px;
            font-family: "MontserratRegular";
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding: 1rem 0rem 0.3rem;
        `,
    },
    styleButton: {
        css: css`
            width: 220px;
        `,
    },
    linkGridItem: {
        width: 12,
        css: css`
            padding: 0px;
            justify-content: center;
            margin-bottom: 4px;
        `,
    },
    imageGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    titleStyles: {
        variant: "h4",
        css: css`
            text-align: center;
            margin-bottom: 0rem;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        `,
    },
    categoryNameLink: {
        typographyProps: {
            css: css`
                display: flex;
                justify-content: center;
            `,
        },
    },
    categoryName: {
        color: "primary",
        size: 14,
        weight: 400,
        css: css`
            font-family: "MontserratRegular";
        `,
    },
    categoryImage: {
        width: "220px",
        height: "210px",
        css: css`
            border-radius: 10px;
        `,
    },
};

export const ImageHoverBox: React.FC<Props> = props => {
    const { url } = useGetLink(props.fields.buttonLink);
    const history = useHistory();
    const onClick = () => {
        if (!url) {
            return;
        }
        if (url.startsWith("http")) {
            window.open(url, "_blank");
        } else {
            props.fields.openNewTab ? window.open(url, "_blank") : history.push(url);
        }
    };

    const style = ImageHoverBoxStyles;
    return (
        <GridContainer {...style.wrapperStyles}>
            <Clickable onClick={onClick} {...style.cickableWrapper}>
                <GridItem {...style.titleGridItem}>
                    <Typography {...style.titleStyles}>{parse(props.fields.title)}</Typography>
                </GridItem>
                <GridItem {...style.imageGridItem}>
                    <LazyImage src={props.fields.image} {...style.categoryImage} />
                </GridItem>
                <GridItem {...style.headingGridItem}>
                    <Typography {...style.headingStyles}>{parse(props.fields.heading)}</Typography>
                </GridItem>
                {!props.fields.disableButton && (
                    <GridItem {...style.buttonGridItem}>
                        <Button {...style.styleButton} variant={props.fields.variant} onClick={onClick}>
                            {props.fields.buttonLabel}
                        </Button>
                    </GridItem>
                )}
                <GridItem {...style.linkGridItem}>
                    <Link onClick={onClick} {...style.categoryNameLink}>
                        <Typography {...style.categoryName}>Know More</Typography>
                    </Link>
                </GridItem>
            </Clickable>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: ImageHoverBox,
    definition: {
        group: "Basic",
        icon: "ImageHoverBox",
        displayName: "Training & Testing Card List",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [{ displayName: "Image", value: "image" }],
                defaultValue: "image",
                hideEmptyOption: true,
            },

            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },

            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.openNewTab,
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },

            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
